import React, {
  forwardRef,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './Select.module.css';

/* ── Types ── */

export type SelectSize   = 'sm' | 'md' | 'lg';
export type SelectStatus = 'default' | 'error' | 'success';

export interface SelectOption {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
}

export interface SelectFooterAction {
  label: React.ReactNode;
  onClick: () => void;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  hint?: string;
  size?: SelectSize;
  status?: SelectStatus;
  disabled?: boolean;
  fullWidth?: boolean;
  id?: string;
  className?: string;
  footerAction?: SelectFooterAction;
}

/* ── Helpers ── */

const cx = (...classes: (string | false | null | undefined)[]) =>
  classes.filter(Boolean).join(' ');

/* ── Chevron icon ── */

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    className={cx(styles.chevron, open && styles.chevronOpen)}
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M4 6l4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── Check icon ── */

const Check = () => (
  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M2.5 7l3.5 3.5 5.5-6"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ── Component ── */

const Select = forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      options,
      value: controlledValue,
      defaultValue = '',
      onChange,
      placeholder = 'Select…',
      label,
      hint,
      size = 'md',
      status = 'default',
      disabled = false,
      fullWidth = false,
      id: externalId,
      className,
      footerAction,
    },
    ref,
  ) => {
    const autoId = useId();
    const id = externalId ?? autoId;
    const labelId = `${id}-label`;
    const listId  = `${id}-listbox`;

    const isControlled = controlledValue !== undefined;
    const [internalValue, setInternalValue] = useState(defaultValue);
    const value = isControlled ? controlledValue : internalValue;

    const [open, setOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>();

    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef  = useRef<HTMLUListElement>(null);

    const selectedOption = options.find(o => o.value === value);
    const enabledOptions = options.filter(o => !o.disabled);

    /* ── Close on outside click ── */
    useEffect(() => {
      if (!open) return;
      const handler = (e: MouseEvent) => {
        const target = e.target as Node;
        const clickedTrigger = containerRef.current?.contains(target);
        const clickedDropdown = dropdownRef.current?.contains(target);
        if (!clickedTrigger && !clickedDropdown) {
          setOpen(false);
          setFocusedIndex(-1);
        }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    useLayoutEffect(() => {
      if (!open) return;

      const updateDropdownPosition = () => {
        const trigger = containerRef.current;
        if (!trigger) return;

        const rect = trigger.getBoundingClientRect();
        const viewportPadding = 8;
        const availableBelow = window.innerHeight - rect.bottom - viewportPadding;
        const availableAbove = rect.top - viewportPadding;
        const maxHeight = Math.max(120, Math.min(240, Math.max(availableBelow, availableAbove)));
        const openUpwards = availableBelow < 160 && availableAbove > availableBelow;

        setDropdownStyle({
          position: 'fixed',
          left: rect.left,
          width: rect.width,
          minWidth: rect.width,
          maxHeight,
          top: openUpwards ? 'auto' : rect.bottom + 4,
          bottom: openUpwards ? window.innerHeight - rect.top + 4 : 'auto',
        });
      };

      updateDropdownPosition();

      window.addEventListener('resize', updateDropdownPosition);
      window.addEventListener('scroll', updateDropdownPosition, true);
      return () => {
        window.removeEventListener('resize', updateDropdownPosition);
        window.removeEventListener('scroll', updateDropdownPosition, true);
      };
    }, [open]);

    /* ── Scroll focused option into view ── */
    useEffect(() => {
      if (!open || focusedIndex < 0) return;
      const item = dropdownRef.current?.children[focusedIndex] as HTMLElement | undefined;
      item?.scrollIntoView({ block: 'nearest' });
    }, [focusedIndex, open]);

    const select = (opt: SelectOption) => {
      if (opt.disabled) return;
      if (!isControlled) setInternalValue(opt.value);
      onChange?.(opt.value);
      setOpen(false);
      setFocusedIndex(-1);
    };

    const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
      if (disabled) return;
      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (open && focusedIndex >= 0) {
            select(options[focusedIndex]);
          } else {
            setOpen(o => !o);
            setFocusedIndex(options.findIndex(o => o.value === value));
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!open) { setOpen(true); setFocusedIndex(0); break; }
          setFocusedIndex(i => {
            const next = options.findIndex((o, idx) => idx > i && !o.disabled);
            return next >= 0 ? next : i;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!open) { setOpen(true); break; }
          setFocusedIndex(i => {
            const candidates = options.map((o, idx) => idx).filter(idx => idx < i && !options[idx].disabled);
            return candidates.length ? candidates[candidates.length - 1] : i;
          });
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          setFocusedIndex(-1);
          break;
        case 'Tab':
          setOpen(false);
          setFocusedIndex(-1);
          break;
      }
    };

    return (
      <div
        ref={ref}
        className={cx(styles.root, fullWidth && styles.fullWidth, className)}
      >
        {label && (
          <label id={labelId} htmlFor={id} className={styles.label}>
            {label}
          </label>
        )}

        <div ref={containerRef} className={cx(styles.container, fullWidth && styles.fullWidth)}>
          {/* ── Trigger ── */}
          <button
            id={id}
            type="button"
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listId}
            aria-labelledby={label ? labelId : undefined}
            aria-disabled={disabled}
            disabled={disabled}
            className={cx(
              styles.trigger,
              styles[size],
              status !== 'default' && styles[status],
              open && styles.triggerOpen,
            )}
            onClick={() => { if (!disabled) { setOpen(o => !o); setFocusedIndex(options.findIndex(o => o.value === value)); } }}
            onKeyDown={handleTriggerKeyDown}
          >
            <span className={cx(styles.triggerValue, !selectedOption && styles.placeholder)}>
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <Chevron open={open} />
          </button>

          {/* ── Dropdown ── */}
          {open &&
            createPortal(
              <ul
                ref={dropdownRef}
                id={listId}
                role="listbox"
                aria-label={label}
                className={styles.dropdown}
                style={dropdownStyle}
              >
                {options.map((opt, idx) => (
                  <li
                    key={opt.value}
                    role="option"
                    aria-selected={opt.value === value}
                    aria-disabled={opt.disabled}
                    className={cx(
                      styles.option,
                      opt.value === value && styles.optionSelected,
                      idx === focusedIndex && styles.optionFocused,
                      opt.disabled && styles.optionDisabled,
                    )}
                    onMouseEnter={() => !opt.disabled && setFocusedIndex(idx)}
                    onMouseDown={e => { e.preventDefault(); select(opt); }}
                  >
                    <span className={styles.optionLabel}>{opt.label}</span>
                    {opt.value === value && (
                      <span className={styles.optionCheck}><Check /></span>
                    )}
                  </li>
                ))}
                {footerAction && (
                  <>
                    <li role="presentation" className={styles.footerDivider} />
                    <li
                      role="presentation"
                      className={styles.footerAction}
                      onMouseDown={e => { e.preventDefault(); footerAction.onClick(); setOpen(false); setFocusedIndex(-1); }}
                    >
                      {footerAction.label}
                    </li>
                  </>
                )}
              </ul>,
              document.body,
            )}
        </div>

        {hint && (
          <span className={cx(styles.hint, status !== 'default' && styles[status])}>
            {hint}
          </span>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';

export { Select };
